/**
 * <product-information-accordion>
 * --------------------------------------------------
 * Wraps a group of accordion items (trigger button + panel pairs) and
 * enforces:
 *   - Only one panel open at a time
 *   - Smooth open/close animation via CSS grid-template-rows (no JS height
 *     measuring — see product-information.css)
 *   - Full keyboard support per the WAI-ARIA accordion pattern:
 *       Enter / Space → toggle
 *       ArrowDown / ArrowUp → move focus to next / previous trigger
 *       Home / End → move focus to first / last trigger
 *   - aria-expanded kept in sync on the trigger
 */
class ProductInformationAccordion extends HTMLElement {
  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll('.product-information-accordion__trigger'));

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.toggle(trigger));
      trigger.addEventListener('keydown', (e) => this.onKeydown(e, trigger));
    });

    // Respect any item marked open by default (data-open="true" on its panel)
    const openTrigger = this.triggers.find((t) => t.getAttribute('aria-expanded') === 'true');
    if (openTrigger) this.open(openTrigger);
  }

  panelFor(trigger) {
    return document.getElementById(trigger.getAttribute('aria-controls'));
  }

  toggle(trigger) {
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      this.close(trigger);
    } else {
      this.open(trigger);
    }
  }

  open(trigger) {

    trigger.setAttribute('aria-expanded', 'true');
    const panel = this.panelFor(trigger);
    if (panel) panel.setAttribute('data-open', 'true');
  }

  close(trigger) {
    trigger.setAttribute('aria-expanded', 'false');
    const panel = this.panelFor(trigger);
    if (panel) panel.setAttribute('data-open', 'false');
  }

  onKeydown(e, trigger) {
    const currentIndex = this.triggers.indexOf(trigger);
    let targetIndex = null;

    switch (e.key) {
      case 'ArrowDown':
        targetIndex = (currentIndex + 1) % this.triggers.length;
        break;
      case 'ArrowUp':
        targetIndex = (currentIndex - 1 + this.triggers.length) % this.triggers.length;
        break;
      case 'Home':
        targetIndex = 0;
        break;
      case 'End':
        targetIndex = this.triggers.length - 1;
        break;
      default:
        return;
    }

    e.preventDefault();
    this.triggers[targetIndex].focus();
  }
}

customElements.define('product-information-accordion', ProductInformationAccordion);
